/**
 * Tibbs's last-card tip: when it is your turn in normal play and one card of
 * yours is still face down, uncovering it (flip, or swap into it) puts you out
 * and gives everyone else one last shot; keeping it hidden lets you keep
 * improving. The tip shows once per game, through the first turn where the
 * moment arises (its stages: before the draw, holding, after a toss), for
 * your first three games. The × closes it early; replaying the coach from
 * Help brings it back.
 */
import type { GameState } from '../../engine';

export const LAST_CARD_TIP = ['Uncover your last card: you’re out, others get one shot.', 'Keep it hidden to keep improving.'] as const;

/** Games in which the tip shows on its own before it retires. */
export const LAST_CARD_TIP_GAMES = 3;

export interface TipMemory {
  /** Games (ids) in which the tip has shown, oldest first. */
  seen: string[];
  /** Games in which the player closed it. */
  dismissed: string[];
  /** Game id → the one turn (see tipTurn) the tip belongs to in that game. */
  turn: Record<string, string>;
}

export const EMPTY_TIP_MEMORY: TipMemory = { seen: [], dismissed: [], turn: {} };

/** Identifies the current turn within a game: hole + completed turns this hole. */
export function tipTurn(state: GameState): string {
  return `${state.hole}:${state.holeTurn}`;
}

/** Read a stored value defensively (any shape). */
export function normalizeTipMemory(raw: unknown): TipMemory {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  const turn: Record<string, string> = {};
  if (o.turn && typeof o.turn === 'object') for (const [k, v] of Object.entries(o.turn)) if (typeof v === 'string') turn[k] = v;
  return { seen: list(o.seen), dismissed: list(o.dismissed), turn };
}

/**
 * The moment the choice is real: your own turn in normal play (not a final
 * turn, when the hole is ending anyway) with exactly one face-down card. It
 * covers the whole turn: before drawing (a discard-pile card must be swapped,
 * maybe into that slot), holding a card, and after a toss (flip or keep hidden).
 */
export function lastCardMoment(state: GameState, me: number | null): boolean {
  if (me === null || state.phase !== 'playing' || state.current !== me || !state.active.includes(me)) return false;
  const grid = state.players[me]?.grid ?? [];
  return grid.length === 8 && grid.filter((s) => !s.faceUp).length === 1;
}

/** Whether the tip may show on this turn: only the first turn it appeared on in this game. */
export function tipAllowed(mem: TipMemory, gameId: string, turn: string): boolean {
  if (mem.dismissed.includes(gameId)) return false;
  const first = mem.turn[gameId];
  if (first !== undefined) return first === turn;
  return !mem.seen.includes(gameId) && mem.seen.length < LAST_CARD_TIP_GAMES;
}

/** Record that the tip showed in this game, on this turn. */
export function tipSeen(mem: TipMemory, gameId: string, turn: string): TipMemory {
  if (mem.turn[gameId] !== undefined) return mem;
  const turns = Object.fromEntries([...Object.entries(mem.turn), [gameId, turn]].slice(-8));
  return { ...mem, seen: mem.seen.includes(gameId) ? mem.seen : [...mem.seen, gameId], turn: turns };
}

/** Closed for the rest of this game (only the last few games are remembered). */
export function tipDismissed(mem: TipMemory, gameId: string): TipMemory {
  if (mem.dismissed.includes(gameId)) return mem;
  return { ...mem, dismissed: [...mem.dismissed, gameId].slice(-8) };
}
